import Close from '@mui/icons-material/Close';
import SouthEast from '@mui/icons-material/SouthEast';
import Box from '@mui/material/Box';
import MuiDialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Button } from '../Button/Button';
import { IconButton } from '../IconButton/IconButton';
import { Stack } from '../Stack/Stack';

interface DialogProps {
  open?: boolean;
  title: string | ReactNode;
  headerContent?: ReactNode;
  frameless?: boolean;
  draggable?: boolean;
  resizable?: boolean;
  cancelText?: string;
  onClose?: () => void;
  confirmText?: string;
  onSubmit?: () => void;
  secondaryActionText?: string;
  onSecondaryAction?: () => void;
  isSubmitting?: boolean;
  children: ReactNode;
  confirmDisabled?: boolean;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
  disableScrollLock?: boolean;
  disableRestoreFocus?: boolean;
  allowBackgroundInteraction?: boolean;
  sx?: object;
  contentSx?: object;
  footer?: ReactNode;
}

export function Dialog({
  open = true,
  title,
  headerContent,
  frameless = false,
  draggable = false,
  resizable = false,
  cancelText,
  onClose = () => {},
  confirmText,
  onSubmit,
  secondaryActionText,
  onSecondaryAction,
  isSubmitting = false,
  children,
  confirmDisabled = false,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  disableScrollLock = true,
  disableRestoreFocus = true,
  allowBackgroundInteraction = false,
  sx,
  contentSx,
  footer,
}: DialogProps) {
  const theme = useTheme();
  const titleId = useId();
  const isActions =
    cancelText || (confirmText && onSubmit) || (secondaryActionText && onSecondaryAction);

  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeAnchor, setResizeAnchor] = useState<{ left: number; top: number } | null>(null);
  // Freeze the initial centered position so resizing moves only the lower-right edge.
  const [resizeSize, setResizeSize] = useState<{ width: number; height: number } | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const resizeStartRef = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
    left: number;
    top: number;
  } | null>(null);
  const setPaperRef = useCallback(
    (node: HTMLDivElement | null) => {
      paperRef.current = node;
      if (!node || !resizable) return;
      const bounds = node.getBoundingClientRect();
      setResizeAnchor(
        (current) => current ?? { left: Math.max(16, bounds.left), top: Math.max(16, bounds.top) }
      );
    },
    [resizable]
  );
  useEffect(() => {
    if (!open || !resizable) {
      setResizeAnchor(null);
      setResizeSize(null);
      setDragOffset({ x: 0, y: 0 });
    }
  }, [open, resizable]);
  const dragStartRef = useRef<{
    x: number;
    y: number;
    startX: number;
    startY: number;
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  const handleDragStart = (event: React.MouseEvent) => {
    if (!draggable) return;

    const paperElement = (event.currentTarget as HTMLElement).closest('.draggable-dialog-paper');
    if (!paperElement) return;

    const bounds = paperElement.getBoundingClientRect();
    dragStartRef.current = {
      left: bounds.left,
      top: bounds.top,
      width: bounds.width,
      height: bounds.height,
      x: dragOffset.x,
      y: dragOffset.y,
      startX: event.clientX,
      startY: event.clientY,
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;
      const start = dragStartRef.current;
      let deltaX = e.clientX - start.startX;
      let deltaY = e.clientY - start.startY;
      if (resizable) {
        deltaX = Math.max(
          16 - start.left,
          Math.min(deltaX, window.innerWidth - 16 - start.left - start.width)
        );
        deltaY = Math.max(
          16 - start.top,
          Math.min(deltaY, window.innerHeight - 16 - start.top - start.height)
        );
      }
      setDragOffset({
        x: dragStartRef.current.x + deltaX,
        y: dragStartRef.current.y + deltaY,
      });
    };

    const handleMouseUp = () => {
      dragStartRef.current = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const resizeWithinViewport = (width: number, height: number, left: number, top: number) => {
    setResizeSize({
      width: Math.max(0, Math.min(width, window.innerWidth - left - 16)),
      height: Math.max(0, Math.min(height, window.innerHeight - top - 16)),
    });
  };
  const paperStyle = sx as CSSProperties | undefined;
  const boundSize = (size: CSSProperties['maxWidth'], available: string, fallback: string) =>
    `min(${typeof size === 'number' ? `${size}px` : (size ?? fallback)}, ${available})`;
  const availableWidth = `calc(100vw - ${(resizeAnchor?.left ?? 0) + dragOffset.x + 16}px)`;
  const availableHeight = `calc(100dvh - ${(resizeAnchor?.top ?? 0) + dragOffset.y + 16}px)`;
  const dialogTitle = (
    <DialogTitle
      sx={
        frameless
          ? visuallyHidden
          : {
              pr: headerContent ? '1rem' : '3rem',
              pb: '0.5rem',
              cursor: draggable ? 'move' : 'default',
              flexShrink: 0,
            }
      }
      id={titleId}
      onMouseDown={draggable ? handleDragStart : undefined}
    >
      {title}
    </DialogTitle>
  );

  return open ? (
    <span
      onClick={(e) => {
        // This wrapper is needed to prevent the click event from propagating to the parent element
        e.stopPropagation();
      }}
    >
      <MuiDialog
        open={true}
        aria-labelledby={titleId}
        hideBackdrop={allowBackgroundInteraction}
        disableScrollLock={disableScrollLock}
        disableRestoreFocus={disableRestoreFocus}
        disableEscapeKeyDown={disableEscapeKeyDown}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') {
            if (!disableBackdropClick) {
              onClose();
            }
          } else {
            onClose();
          }
        }}
        maxWidth={false}
        slotProps={{
          root: {
            style: {
              pointerEvents: allowBackgroundInteraction ? 'none' : undefined,
            },
          },
          paper: {
            className: 'draggable-dialog-paper',
            ref: setPaperRef,
            style: {
              transform: draggable ? `translate(${dragOffset.x}px, ${dragOffset.y}px)` : undefined,
              margin: 'auto',
              pointerEvents: 'auto',
              backgroundColor: theme.palette.background.paper,
              backgroundImage: 'none',
              ...(frameless ? { border: 0, borderRadius: '0.75rem' } : {}),
              resize: resizable ? 'none' : undefined,
              overflow: resizable ? 'hidden' : undefined,
              ...sx,
              ...(resizable && resizeSize ? resizeSize : {}),
              ...(resizable && resizeAnchor
                ? {
                    position: 'fixed',
                    left: resizeAnchor.left + dragOffset.x,
                    top: resizeAnchor.top + dragOffset.y,
                    transform: 'none',
                    margin: 0,
                    maxWidth: boundSize(paperStyle?.maxWidth, availableWidth, 'calc(100vw - 32px)'),
                    maxHeight: boundSize(
                      paperStyle?.maxHeight,
                      availableHeight,
                      'calc(100dvh - 32px)'
                    ),
                    minWidth: boundSize(paperStyle?.minWidth, availableWidth, '0px'),
                    minHeight: boundSize(paperStyle?.minHeight, availableHeight, '0px'),
                  }
                : {}),
            },
          },
        }}
      >
        {headerContent ? (
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="nowrap"
            sx={{ pr: '3rem', minWidth: 0, flexShrink: 0 }}
          >
            {dialogTitle}
            <Box sx={{ flex: 1, minWidth: 0 }}>{headerContent}</Box>
          </Stack>
        ) : (
          dialogTitle
        )}

        <Stack
          position="absolute"
          top="0.5rem"
          right="0.5rem"
          sx={
            frameless
              ? { zIndex: 1100, borderRadius: '50%', bgcolor: 'background.paper', boxShadow: 2 }
              : undefined
          }
        >
          <IconButton icon={Close} onClick={onClose} ariaLabel="Close" />
        </Stack>

        <DialogContent
          sx={{
            pt: '0.5rem',
            pb: isActions ? '0rem' : '1rem',
            ...(frameless ? { p: 0 } : {}),
            ...contentSx,
          }}
        >
          {children}

          {isActions && (
            <DialogActions
              sx={{
                px: '0',
                py: '1.5rem',
                mt: '1rem',
                justifyContent: 'space-between',
              }}
            >
              {cancelText && (
                <Button
                  variant="outlined"
                  onClick={(e) => {
                    e?.stopPropagation();
                    onClose();
                  }}
                >
                  {cancelText}
                </Button>
              )}
              <Stack direction="row" gap="1rem">
                {secondaryActionText && onSecondaryAction && (
                  <Button variant="outlined" onClick={onSecondaryAction}>
                    {secondaryActionText}
                  </Button>
                )}

                {confirmText && onSubmit && (
                  <Button onClick={onSubmit} isLoading={isSubmitting} disabled={confirmDisabled}>
                    {confirmText}
                  </Button>
                )}
              </Stack>
            </DialogActions>
          )}
        </DialogContent>
        {footer && (
          <DialogActions
            sx={{ borderTop: 1, borderColor: 'divider', padding: '1rem 1.5rem', flexShrink: 0 }}
          >
            {footer}
          </DialogActions>
        )}
        {resizable && (
          <Box
            component="button"
            type="button"
            aria-label="Resize dialog"
            onPointerDown={(event) => {
              if (event.button !== 0) return;
              const paper = paperRef.current;
              if (!paper) return;
              event.preventDefault();
              const bounds = paper.getBoundingClientRect();
              resizeStartRef.current = {
                x: event.clientX,
                y: event.clientY,
                width: bounds.width,
                height: bounds.height,
                left: bounds.left,
                top: bounds.top,
              };
              event.currentTarget.setPointerCapture(event.pointerId);
            }}
            onPointerMove={(event) => {
              const start = resizeStartRef.current;
              if (!start) return;
              resizeWithinViewport(
                start.width + event.clientX - start.x,
                start.height + event.clientY - start.y,
                start.left,
                start.top
              );
            }}
            onPointerUp={() => {
              resizeStartRef.current = null;
            }}
            onPointerCancel={() => {
              resizeStartRef.current = null;
            }}
            onLostPointerCapture={() => {
              resizeStartRef.current = null;
            }}
            onKeyDown={(event) => {
              if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
              event.preventDefault();
              const bounds = paperRef.current?.getBoundingClientRect();
              if (!bounds) return;
              const step = event.shiftKey ? 32 : 16;
              resizeWithinViewport(
                bounds.width +
                  (event.key === 'ArrowRight' ? step : event.key === 'ArrowLeft' ? -step : 0),
                bounds.height +
                  (event.key === 'ArrowDown' ? step : event.key === 'ArrowUp' ? -step : 0),
                bounds.left,
                bounds.top
              );
            }}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              zIndex: 2,
              width: '1rem',
              height: '1rem',
              p: 0,
              border: 0,
              bgcolor: 'transparent',
              color: 'text.secondary',
              cursor: 'nwse-resize',
              touchAction: 'none',
              display: 'grid',
              placeItems: 'center',
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '-2px',
              },
            }}
          >
            <SouthEast sx={{ fontSize: '0.75rem' }} />
          </Box>
        )}
      </MuiDialog>
    </span>
  ) : null;
}
